import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { geoApi } from "./api";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const initialState = {
  projects: [],
  activeProject: null,
  prompts: [],
  runs: [],
  results: [],
  analytics: [],
  insights: [],
  shareOfVoice: null,
  competitorAnalytics: [],
  promptStatuses: [],
  loading: false,
  error: null,
};

export const fetchProjects = createAsyncThunk(
  "geo/fetchProjects",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await geoApi.getProjects(clientId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch projects"));
    }
  }
);

export const fetchPrompts = createAsyncThunk(
  "geo/fetchPrompts",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await geoApi.getPrompts(projectId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch prompts"));
    }
  }
);

export const fetchRuns = createAsyncThunk(
  "geo/fetchRuns",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await geoApi.getRuns(projectId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch runs"));
    }
  }
);

export const fetchResults = createAsyncThunk(
  "geo/fetchResults",
  async (runId, { rejectWithValue }) => {
    try {
      const response = await geoApi.getResults(runId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch results"));
    }
  }
);

export const fetchAnalytics = createAsyncThunk(
  "geo/fetchAnalytics",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await geoApi.getAnalytics(projectId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch analytics"));
    }
  }
);

export const fetchShareOfVoice = createAsyncThunk(
  "geo/fetchShareOfVoice",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await geoApi.getShareOfVoice(projectId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch share of voice"));
    }
  }
);

export const fetchCompetitorAnalytics = createAsyncThunk(
  "geo/fetchCompetitorAnalytics",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await geoApi.getCompetitorAnalytics(projectId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch competitor analytics"));
    }
  }
);

export const fetchInsights = createAsyncThunk(
  "geo/fetchInsights",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await geoApi.getInsights(projectId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch insights"));
    }
  }
);

export const markInsightActioned = createAsyncThunk(
  "geo/markInsightActioned",
  async ({ insightId, actioned = true }, { rejectWithValue }) => {
    try {
      const response = await geoApi.markInsightActioned(insightId, actioned);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update insight"));
    }
  }
);

export const fetchPromptStatuses = createAsyncThunk(
  "geo/fetchPromptStatuses",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await geoApi.getPromptStatus(projectId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch prompt statuses"));
    }
  }
);

export const triggerRun = createAsyncThunk(
  "geo/triggerRun",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await geoApi.triggerRun(projectId);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to trigger run"));
    }
  }
);

export const updatePromptStatus = createAsyncThunk(
  "geo/updatePromptStatus",
  async ({ projectId, promptId, payload }, { rejectWithValue }) => {
    try {
      const response = await geoApi.updatePrompt(projectId, promptId, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update prompt"));
    }
  }
);

export const updateProject = createAsyncThunk(
  "geo/updateProject",
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await geoApi.updateProject(projectId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update project"));
    }
  }
);

export const deleteProject = createAsyncThunk(
  "geo/deleteProject",
  async (projectId, { rejectWithValue }) => {
    try {
      const res = await geoApi.deleteProject(projectId);
      return { projectId, data: res.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to delete project"));
    }
  }
);

export const cancelRun = createAsyncThunk(
  "geo/cancelRun",
  async (runId, { rejectWithValue }) => {
    try {
      const res = await geoApi.cancelRun(runId);
      return { runId, ...(res.data || {}) };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to cancel run"));
    }
  }
);

const geoSlice = createSlice({
  name: "geo",
  initialState,
  reducers: {
    setActiveProject: (state, action) => {
      state.activeProject = action.payload;
      state.prompts = [];
      state.runs = [];
      state.results = [];
      state.analytics = [];
      state.insights = [];
      state.shareOfVoice = null;
      state.competitorAnalytics = [];
      state.promptStatuses = [];
    },
    setInsightActionedLocally: (state, action) => {
      const insightId = action.payload;
      state.insights = state.insights.map((insight) =>
        insight._id === insightId ? { ...insight, actioned: true } : insight
      );
    },
    clearGeoError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.projects = [];
        state.error = action.payload;
      })
      .addCase(fetchPrompts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrompts.fulfilled, (state, action) => {
        state.loading = false;
        state.prompts = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPrompts.rejected, (state, action) => {
        state.loading = false;
        state.prompts = [];
        state.error = action.payload;
      })
      .addCase(fetchRuns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRuns.fulfilled, (state, action) => {
        state.loading = false;
        state.runs = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchRuns.rejected, (state, action) => {
        state.loading = false;
        state.runs = [];
        state.error = action.payload;
      })
      .addCase(fetchResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.loading = false;
        state.results = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchResults.rejected, (state, action) => {
        state.loading = false;
        state.results = [];
        state.error = action.payload;
      })
      .addCase(fetchAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.analytics = [];
        state.error = action.payload;
      })
      .addCase(fetchShareOfVoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShareOfVoice.fulfilled, (state, action) => {
        state.loading = false;
        state.shareOfVoice = action.payload || null;
      })
      .addCase(fetchShareOfVoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCompetitorAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompetitorAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.competitorAnalytics = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchCompetitorAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.competitorAnalytics = [];
        state.error = action.payload;
      })
      .addCase(fetchInsights.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInsights.fulfilled, (state, action) => {
        state.loading = false;
        state.insights = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchInsights.rejected, (state, action) => {
        state.loading = false;
        state.insights = [];
        state.error = action.payload;
      })
      .addCase(markInsightActioned.pending, (state) => {
        state.error = null;
      })
      .addCase(markInsightActioned.fulfilled, (state, action) => {
        const updatedInsight = action.payload;
        if (!updatedInsight?._id) return;
        state.insights = state.insights.map((insight) =>
          insight._id === updatedInsight._id ? { ...insight, ...updatedInsight } : insight
        );
      })
      .addCase(markInsightActioned.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchPromptStatuses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromptStatuses.fulfilled, (state, action) => {
        state.loading = false;
        state.promptStatuses = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPromptStatuses.rejected, (state, action) => {
        state.loading = false;
        state.promptStatuses = [];
        state.error = action.payload;
      })
      .addCase(triggerRun.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(triggerRun.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.runs = [action.payload, ...state.runs];
        }
      })
      .addCase(triggerRun.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePromptStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePromptStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedPrompt = action.payload;
        state.prompts = state.prompts.map((prompt) =>
          prompt._id === updatedPrompt._id ? updatedPrompt : prompt
        );
      })
      .addCase(updatePromptStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateProject.pending, (state) => {
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        const updatedProject = action.payload;
        if (!updatedProject?._id) return;

        state.projects = state.projects.map((project) =>
          project._id === updatedProject._id ? { ...project, ...updatedProject } : project
        );

        if (state.activeProject?._id === updatedProject._id) {
          state.activeProject = { ...state.activeProject, ...updatedProject };
        }
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteProject.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        const { projectId } = action.payload || {};
        if (!projectId) return;

        state.projects = state.projects.filter((project) => project._id !== projectId);

        if (state.activeProject?._id === projectId) {
          state.activeProject = null;
          state.prompts = [];
          state.runs = [];
          state.results = [];
          state.analytics = [];
          state.insights = [];
          state.shareOfVoice = null;
          state.competitorAnalytics = [];
          state.promptStatuses = [];
        }
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(cancelRun.pending, (state) => {
        state.error = null;
      })
      .addCase(cancelRun.fulfilled, (state, action) => {
        const { runId } = action.payload || {};
        if (!runId) return;

        state.runs = state.runs.map((run) =>
          run._id === runId ? { ...run, status: "failed", error: "Cancelled by user" } : run
        );
      })
      .addCase(cancelRun.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setActiveProject, setInsightActionedLocally, clearGeoError } = geoSlice.actions;

export default geoSlice.reducer;
